import { useState } from "react";
import { LogOut, Users, ChevronDown, LogIn } from "lucide-react";
import { useApp } from "@/store/AppStore";
import { useAuth } from "@/store/AuthStore";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TeamDialog } from "@/components/TeamDialog";

export function TopBar({ onLogin }) {
  const { activeOrder } = useApp();
  const { user, logout } = useAuth();
  const [teamOpen, setTeamOpen] = useState(false);
  const count = activeOrder?.lines?.length || 0;
  const initials = (user?.name || user?.username || "?").slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-slate-800 bg-[#090D16]/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500 shadow-lg shadow-orange-500/30">
            <span className="font-display text-lg font-black text-slate-950">TR</span>
          </div>
          <div className="leading-none">
            <div className="font-display text-xl font-extrabold tracking-tight text-white" data-testid="app-brand">
              TR <span className="text-orange-500">BOLT</span>calc
            </div>
            <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
              Fastener Material Calculator
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {count > 0 && (
            <span data-testid="active-order-badge" className="hidden rounded-full bg-orange-500/15 px-2.5 py-1 text-xs font-bold text-orange-300 sm:inline">
              {count} line{count > 1 ? "s" : ""}
            </span>
          )}

          {!user ? (
            <button
              data-testid="member-login-btn"
              onClick={onLogin}
              className="flex items-center gap-2 rounded-full bg-orange-500 px-4 py-1.5 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 hover:bg-orange-400"
            >
              <LogIn className="h-4 w-4" /> Member Login
            </button>
          ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 py-1 pl-1 pr-2 hover:border-slate-600">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-xs font-black text-slate-950">{initials}</span>
                <span className="hidden max-w-[100px] truncate text-sm font-semibold text-slate-200 sm:inline">{user?.name}</span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 border-slate-700 bg-[#131C2E] text-slate-200">
              <div className="px-2 py-1.5">
                <div className="truncate text-sm font-semibold text-white">{user?.name}</div>
                <div className="truncate text-xs text-slate-500">@{user?.username}</div>
                <div className="mt-1 inline-block rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-300">{user?.role}</div>
              </div>
              <DropdownMenuSeparator className="bg-slate-800" />
              {user?.role === "admin" && (
                <DropdownMenuItem data-testid="open-team-btn" onSelect={() => setTeamOpen(true)} className="cursor-pointer focus:bg-slate-800">
                  <Users className="mr-2 h-4 w-4" /> Manage Team
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="logout-btn" onSelect={logout} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-400">
                <LogOut className="mr-2 h-4 w-4" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>

      <TeamDialog open={teamOpen} onOpenChange={setTeamOpen} />
    </header>
  );
}
